import React, { useState, useCallback, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    Button,
    Box,
    RadioGroup,
    Radio,
    FormControlLabel,
    Switch,
    Chip,
    OutlinedInput,
    FormHelperText,
} from "@mui/material";
import { motion } from "framer-motion";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Star from "@mui/icons-material/Star";
import axios from "axios";
import { localAxiosGet } from "../../../Axios/axios";
import DeleteIcon from "@mui/icons-material/Delete";
// import CheckBox from "@mui/icons-material/CheckBox"; // ❌ Removed
import { useTheme } from "@mui/material/styles";

// --- Dummy Data (Replace with your actual fetch) ---
const DUMMY_EMPLOYEES = [
    { id: "e1", name: "Alice Johnson", deptId: "d1", teamId: "t1", desigId: "g1" },
    { id: "e2", name: "Bob Smith", deptId: "d1", teamId: "t1", desigId: "g1" },
    { id: "e3", name: "Charlie Brown", deptId: "d1", teamId: "t2", desigId: "g2" },
    { id: "e4", name: "Diana Prince", deptId: "d2", teamId: "t3", desigId: "g3" },
    { id: "e5", name: "Ethan Hunt", deptId: "d2", teamId: "t3", desigId: "g4" },
    { id: "e6", name: "Fiona Apple", deptId: "d3", teamId: "t4", desigId: "g5" },
];

const DUMMY_DEPARTMENTS = [
    { id: "d1", name: "Sales" },
    { id: "d2", name: "Engineering" },
    { id: "d3", name: "HR" },
];

const DUMMY_TEAMS = [
    { id: "t1", name: "North America Sales", deptId: "d1" },
    { id: "t2", name: "APAC Sales", deptId: "d1" },
    { id: "t3", name: "Frontend Dev", deptId: "d2" },
    { id: "t4", name: "Recruitment", deptId: "d3" },
];

const DUMMY_DESIGNATIONS = [
    { id: "g1", name: "Sales Associate", teamId: "t1" },
    { id: "g2", name: "Sales Manager", teamId: "t2" },
    { id: "g3", name: "Senior Developer", teamId: "t3" },
    { id: "g4", name: "Junior Developer", teamId: "t3" },
    { id: "g5", name: "HR Specialist", teamId: "t4" },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};
// --- End Dummy Data ---

// Helper functions for dummy data filtering
const getTeamsForSelectedDepartments = (deptIds) => {
    if (deptIds.length === 0) return [];
    return DUMMY_TEAMS.filter(team => deptIds.includes(team.deptId));
};

const getDesignationsForSelectedTeams = (teamIds) => {
    if (teamIds.length === 0) return [];
    return DUMMY_DESIGNATIONS.filter(designation => teamIds.includes(designation.teamId));
};

const filterEmployees = (deptIds, teamIds, desigIds) => {
    let employees = DUMMY_EMPLOYEES;

    if (deptIds.length > 0) {
        employees = employees.filter(emp => deptIds.includes(emp.deptId));
    }
    if (teamIds.length > 0) {
        employees = employees.filter(emp => teamIds.includes(emp.teamId));
    }
    if (desigIds.length > 0) {
        employees = employees.filter(emp => desigIds.includes(emp.desigId));
    }

    return employees;
};

const AddHrGoal = ({ open = true, onClose }) => {
    const theme = useTheme();
    const [goalType, setGoalType] = useState("");
    const [goalTitle, setGoalTitle] = useState("");
    const [description, setDescription] = useState("");
    const [selectionMode, setSelectionMode] = useState("single");
    const [selectAllEmployees, setSelectAllEmployees] = useState(false);

    // State for multiple selection (Arrays)
    const [departments, setDepartments] = useState(DUMMY_DEPARTMENTS);
    const [teams, setTeams] = useState(DUMMY_TEAMS);
    const [designations, setDesignations] = useState(DUMMY_DESIGNATIONS);

    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedDesignations, setSelectedDesignations] = useState([]);

    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState(DUMMY_EMPLOYEES);
    
    // State to control the open/close behavior of the "single mode" Select
    const [isSingleSelectOpen, setIsSingleSelectOpen] = useState(false);

    const token = localStorage.getItem("accessToken");
    const orgId = localStorage.getItem("organizationId");

    // --- Data Fetch/Filter Effects ---

    useEffect(() => {
        setTeams(getTeamsForSelectedDepartments(selectedDepartments));
        if (selectedTeams.some(tId => !getTeamsForSelectedDepartments(selectedDepartments).map(t => t.id).includes(tId))) {
            setSelectedTeams([]);
            setSelectedDesignations([]);
        }
    }, [selectedDepartments]);

    useEffect(() => {
        setDesignations(getDesignationsForSelectedTeams(selectedTeams));
        if (selectedDesignations.some(dId => !getDesignationsForSelectedTeams(selectedTeams).map(d => d.id).includes(dId))) {
            setSelectedDesignations([]);
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (selectionMode === "multiple") {
            const newFilteredEmployees = filterEmployees(
                selectedDepartments,
                selectedTeams,
                selectedDesignations
            );
            setFilteredEmployees(newFilteredEmployees);

            setSelectedEmployees(prev =>
                prev.filter(empId => newFilteredEmployees.some(emp => emp.id === empId))
            );
        } else {
            setFilteredEmployees(DUMMY_EMPLOYEES);
        }
    }, [selectedDepartments, selectedTeams, selectedDesignations, selectionMode]);

    useEffect(() => {
        if (selectionMode === "multiple" && selectAllEmployees) {
            setSelectedEmployees(filteredEmployees.map(emp => emp.id));
        }
    }, [selectAllEmployees, filteredEmployees, selectionMode]);

    // --- Handlers ---

    const handleGoalTypeChange = (event) => {
        setGoalType(event.target.value);
        setGoalTitle("");
        setDescription("");
    };

    const handleEmployeeDelete = (employeeId) => {
        setSelectedEmployees((prev) => prev.filter((id) => id !== employeeId));
    };
    
    const handleEmployeeSelectionChange = (event) => {
        setSelectedEmployees(event.target.value);
        
        // Force the dropdown to close only in "single" mode (as requested previously)
        if (selectionMode === "single") {
            setTimeout(() => {
                setIsSingleSelectOpen(false);
            }, 50); 
        }
    };

    const handleSelectionModeChange = (event) => {
        const mode = event.target.value;
        setSelectionMode(mode);
        setSelectedDepartments([]);
        setSelectedTeams([]);
        setSelectedDesignations([]);
        setSelectAllEmployees(false);
    };

    const handleSubmit = () => {
        const assignedTo =
            selectionMode === "single"
                ? { employeeIds: selectedEmployees }
                : {
                      departmentIds: selectedDepartments,
                      teamIds: selectedTeams,
                      designationIds: selectedDesignations,
                      employeeIds: selectAllEmployees ? filteredEmployees.map(e => e.id) : selectedEmployees,
                      selectAll: selectAllEmployees,
                  };

        const goalData = {
            goalType,
            goalTitle,
            description,
            selectionMode,
            assignedTo,
        };

        console.log("Goal Submitted:", goalData);
        onClose && onClose();
    };

    const getEmployeeNameById = (id) => {
        return DUMMY_EMPLOYEES.find(e => e.id === id)?.name || "Unknown Employee";
    };

    const isSaveDisabled = !goalType || !goalTitle || !description || selectedEmployees.length === 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            {/* Header */}
            <DialogTitle
                sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box display="flex" alignItems="center">
                    <Add sx={{ mr: 1 }} />
                    Add HR Goal
                </Box>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Body */}
            <DialogContent sx={{ p: 4 }}>
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 3, color: "primary.main" }}
                >
                    Goal Details
                </Typography>

                {/* Goal Type Dropdown */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="goal-type-label">Goal Type</InputLabel>
                    <Select
                        labelId="goal-type-label"
                        id="goal-type"
                        value={goalType}
                        label="Goal Type"
                        onChange={handleGoalTypeChange}
                    >
                        <MenuItem value="Predefined Business Goal">
                            <Box display="flex" alignItems="center">
                                <TrendingUp sx={{ mr: 1 }} />
                                Predefined Business Goal
                            </Box>
                        </MenuItem>
                        <MenuItem value="Predefined Personal Goal">
                            <Box display="flex" alignItems="center">
                                <Star sx={{ mr: 1 }} />
                                Predefined Personal Goal
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>

                {goalType && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Grid container spacing={2}>
                            {/* Goal Title */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Goal Title"
                                    value={goalTitle}
                                    onChange={(e) => setGoalTitle(e.target.value)}
                                />
                            </Grid>

                            {/* Description */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    multiline
                                    minRows={4}
                                />
                            </Grid>

                            {/* --- Assigned To Section (Mode Selection) --- */}
                            <Grid item xs={12} sx={{ mt: 3, mb: 1 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: "primary.main" }}>
                                    Assign To
                                </Typography>
                                <RadioGroup
                                    row
                                    value={selectionMode}
                                    onChange={handleSelectionModeChange}
                                >
                                    <FormControlLabel value="single" control={<Radio />} label="Select Employees Directly" />
                                    <FormControlLabel value="multiple" control={<Radio />} label="Filter by Dept/Team/Designation" />
                                </RadioGroup>
                            </Grid>

                            {/* --- Employee Selection Dropdown (For Single mode) --- */}
                            {selectionMode === "single" && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="employee-selection-label">Select Employees</InputLabel>
                                        <Select
                                            labelId="employee-selection-label"
                                            multiple
                                            open={isSingleSelectOpen}
                                            onOpen={() => setIsSingleSelectOpen(true)}
                                            onClose={() => setIsSingleSelectOpen(false)}
                                            value={selectedEmployees}
                                            onChange={handleEmployeeSelectionChange} 
                                            input={<OutlinedInput label="Select Employees" />}
                                            renderValue={() => "Select employees to assign this goal"}
                                            MenuProps={MenuProps}
                                        >
                                            {DUMMY_EMPLOYEES.map((employee) => (
                                                <MenuItem 
                                                    key={employee.id} 
                                                    value={employee.id}
                                                    // This style shows a checkmark icon to indicate selection
                                                    selected={selectedEmployees.includes(employee.id)} 
                                                >
                                                    {/* ❌ REMOVED: <CheckBox checked={selectedEmployees.includes(employee.id)} /> */}
                                                    {employee.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>Select the employees to assign this goal to.</FormHelperText>
                                    </FormControl>
                                </Grid>
                            )}
                            
                            {/* --- Multiple Assignment Flow (Department/Team/Designation) --- */}
                            {selectionMode === "multiple" && (
                                <>
                                    {/* Department Dropdown (Multi-select) */}
                                    <Grid item xs={12} md={4} sx={{ mt: 1 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="department-label">Department</InputLabel>
                                            <Select
                                                labelId="department-label"
                                                multiple
                                                value={selectedDepartments}
                                                onChange={(e) => setSelectedDepartments(e.target.value)}
                                                input={<OutlinedInput label="Department" />}
                                                renderValue={(selected) => selected.map(id => DUMMY_DEPARTMENTS.find(d => d.id === id)?.name).join(', ')}
                                                MenuProps={MenuProps}
                                            >
                                                {DUMMY_DEPARTMENTS.map((dept) => (
                                                    <MenuItem 
                                                        key={dept.id} 
                                                        value={dept.id}
                                                        selected={selectedDepartments.includes(dept.id)}
                                                    >
                                                        {/* ❌ Checkbox removed from D/T/D selectors too for consistency */}
                                                        {dept.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Team Dropdown (Multi-select) */}
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth disabled={teams.length === 0}>
                                            <InputLabel id="team-label">Team</InputLabel>
                                            <Select
                                                labelId="team-label"
                                                multiple
                                                value={selectedTeams}
                                                onChange={(e) => setSelectedTeams(e.target.value)}
                                                input={<OutlinedInput label="Team" />}
                                                renderValue={(selected) => selected.map(id => DUMMY_TEAMS.find(t => t.id === id)?.name).join(', ')}
                                                MenuProps={MenuProps}
                                            >
                                                {teams.map((team) => (
                                                    <MenuItem 
                                                        key={team.id} 
                                                        value={team.id}
                                                        selected={selectedTeams.includes(team.id)}
                                                    >
                                                        {/* ❌ Checkbox removed */}
                                                        {team.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {selectedDepartments.length === 0 && <FormHelperText>Select a Department first</FormHelperText>}
                                        </FormControl>
                                    </Grid>

                                    {/* Designation Dropdown (Multi-select) */}
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth disabled={designations.length === 0}>
                                            <InputLabel id="designation-label">Designation</InputLabel>
                                            <Select
                                                labelId="designation-label"
                                                multiple
                                                value={selectedDesignations}
                                                onChange={(e) => setSelectedDesignations(e.target.value)}
                                                input={<OutlinedInput label="Designation" />}
                                                renderValue={(selected) => selected.map(id => DUMMY_DESIGNATIONS.find(d => d.id === id)?.name).join(', ')}
                                                MenuProps={MenuProps}
                                            >
                                                {designations.map((d) => (
                                                    <MenuItem 
                                                        key={d.id} 
                                                        value={d.id}
                                                        selected={selectedDesignations.includes(d.id)}
                                                    >
                                                        {/* ❌ Checkbox removed */}
                                                        {d.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {selectedTeams.length === 0 && <FormHelperText>Select a Team first</FormHelperText>}
                                        </FormControl>
                                    </Grid>

                                    {/* Select All Employees Switch and Employee Multi-select */}
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Typography variant="subtitle1" color="text.secondary">
                                                Employee Selection
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={selectAllEmployees}
                                                        onChange={(e) => setSelectAllEmployees(e.target.checked)}
                                                        color="primary"
                                                        disabled={filteredEmployees.length === 0}
                                                    />
                                                }
                                                label={`Select All Employees (${filteredEmployees.length})`}
                                            />
                                        </Box>

                                        {!selectAllEmployees && (
                                            <FormControl fullWidth>
                                                <InputLabel id="employee-label">Select Employees</InputLabel>
                                                <Select
                                                    labelId="employee-label"
                                                    multiple
                                                    value={selectedEmployees}
                                                    onChange={handleEmployeeSelectionChange}
                                                    input={<OutlinedInput label="Select Employees" />}
                                                    renderValue={() => `Selected: ${selectedEmployees.length} employees`}
                                                    MenuProps={MenuProps}
                                                    disabled={filteredEmployees.length === 0}
                                                >
                                                    {filteredEmployees.map((employee) => (
                                                        <MenuItem 
                                                            key={employee.id} 
                                                            value={employee.id}
                                                            selected={selectedEmployees.includes(employee.id)}
                                                        >
                                                            {/* ❌ Checkbox removed */}
                                                            {employee.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {filteredEmployees.length === 0 && (
                                                    <FormHelperText error>No employees match the current D/T/D filter.</FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    </Grid>
                                </>
                            )}
                            {/* --- End Multiple Assignment Flow --- */}

                            {/* --- Employee Chip Display (Visible in BOTH modes) --- */}
                            {(selectedEmployees.length > 0) && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Assigned To:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {selectedEmployees.map((employeeId) => (
                                            <Chip
                                                key={employeeId}
                                                label={getEmployeeNameById(employeeId)}
                                                onDelete={() => handleEmployeeDelete(employeeId)}
                                                deleteIcon={<DeleteIcon />}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            )}

                        </Grid>
                    </motion.div>
                )}

                {/* Buttons */}
                <Grid container justifyContent="flex-end" sx={{ mt: 5, gap: 2 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSaveDisabled}
                        onClick={handleSubmit}
                    >
                        Save Goal
                    </Button>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default AddHrGoal;